import React, { useState, useEffect, useMemo } from 'react';
import {
  Box,
  Typography,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  FormControlLabel,
  Switch,
  Chip,
  Stack,
  CircularProgress,
  Tabs,
  Tab,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Tooltip,
  InputAdornment,
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  ExpandMore as ExpandMoreIcon,
  Search as SearchIcon,
  CheckCircle as CheckCircleIcon,
  VisibilityOff as HiddenIcon,
  Visibility as VisibleIcon,
} from '@mui/icons-material';
import { toast } from 'react-toastify';
import fieldMappingService, {
  FieldMapping,
  CreateFieldMappingData,
  FieldTypes,
  Sections,
  SectionSetting,
  UpdateSectionSettingsData,
} from '../services/fieldMapping.service';

interface FieldMappingManagerProps {
  intakeFormTypeId: number;
  intakeFormTypeName: string;
}

const SECTION_ICONS: Record<string, string> = {
  personal_info: '👤',
  legal_issues: '⚖️',
  education_training: '🎓',
  work_history: '💼',
  previous_employment_history: '🗂️',
  employment_post_injury: '🏥',
  job_search_information: '🔍',
  benefits_information: '💰',
  medical_history: '🩺',
  therapy_information: '🧠',
  activities_daily_living: '🏃',
  hospitalization_information: '🏨',
  signatures: '✍️',
};

const emptyForm: CreateFieldMappingData = {
  form_field: '',
  label: '',
  template_variable: '',
  field_type: 'text',
  section: '',
  is_required: false,
  is_hidden: false,
  description: '',
  sort_order: 0,
};

const FieldMappingManager: React.FC<FieldMappingManagerProps> = ({
  intakeFormTypeId,
  intakeFormTypeName,
}) => {
  const [mappings, setMappings]           = useState<FieldMapping[]>([]);
  const [fieldTypes, setFieldTypes]       = useState<FieldTypes>({});
  const [sections, setSections]           = useState<Sections>({});
  const [loading, setLoading]             = useState(false);
  const [togglingId, setTogglingId]       = useState<number | null>(null);
  const [openDialog, setOpenDialog]       = useState(false);
  const [editingMapping, setEditingMapping] = useState<FieldMapping | null>(null);
  const [formData, setFormData]           = useState<CreateFieldMappingData>(emptyForm);

  // Section visibility tab state
  const [sectionSettings, setSectionSettings]   = useState<SectionSetting[]>([]);
  const [loadingSections, setLoadingSections]   = useState(false);
  const [savingSections, setSavingSections]     = useState(false);

  // UI state
  const [activeTab, setActiveTab]         = useState(0);
  const [search, setSearch]               = useState('');
  const [expandedSections, setExpandedSections] = useState<Set<string>>(new Set());

  // ── Data loading ──────────────────────────────────────────────────────────
  useEffect(() => {
    loadMappings();
    loadSectionSettings();
  }, [intakeFormTypeId]);

  const loadMappings = async () => {
    setLoading(true);
    try {
      const response = await fieldMappingService.getByIntakeFormType(intakeFormTypeId);
      const data = Array.isArray(response.data.data) ? response.data.data : [];
      setMappings(data);
      setFieldTypes(fieldMappingService.getFieldTypes());
      const s = fieldMappingService.getSections();
      setSections(s);
      // Auto-expand all sections on first load
      setExpandedSections(new Set(Object.keys(s)));
    } catch {
      toast.error('Failed to load field mappings');
    } finally {
      setLoading(false);
    }
  };

  const loadSectionSettings = async () => {
    setLoadingSections(true);
    try {
      const response = await fieldMappingService.getSectionSettings(intakeFormTypeId);
      const settings = response.data.data || [];
      if (settings.length === 0) {
        const allSections = response.data.sections || {};
        setSectionSettings(
          Object.entries(allSections).map(([key, name]) => ({
            section: key, section_name: name as string, is_hidden: false, sort_order: 0,
          }))
        );
      } else {
        setSectionSettings(settings);
      }
    } catch {
      // silently fail — not critical
    } finally {
      setLoadingSections(false);
    }
  };

  // ── Grouped mappings ──────────────────────────────────────────────────────
  const groupedMappings = useMemo(() => {
    const q = search.toLowerCase();
    const filtered = mappings.filter(
      m =>
        !q ||
        m.form_field.toLowerCase().includes(q) ||
        (m.label || '').toLowerCase().includes(q) ||
        m.template_variable.toLowerCase().includes(q)
    );
    const groups: Record<string, FieldMapping[]> = {};
    filtered.forEach(m => {
      const key = m.section || '__none__';
      if (!groups[key]) groups[key] = [];
      groups[key].push(m);
    });
    return groups;
  }, [mappings, search]);

  const sectionKeys = useMemo(
    () => Object.keys(groupedMappings).sort(),
    [groupedMappings]
  );

  // ── Inline toggles (immediate save) ──────────────────────────────────────
  const handleToggle = async (
    mapping: FieldMapping,
    field: 'is_required' | 'is_hidden'
  ) => {
    setTogglingId(mapping.id);
    try {
      await fieldMappingService.update(intakeFormTypeId, mapping.id, {
        ...mapping,
        id: mapping.id,
        [field]: !mapping[field],
      });
      setMappings(prev =>
        prev.map(m => (m.id === mapping.id ? { ...m, [field]: !m[field] } : m))
      );
    } catch {
      toast.error('Failed to update field');
    } finally {
      setTogglingId(null);
    }
  };

  // ── CRUD ──────────────────────────────────────────────────────────────────
  const handleCreate = () => {
    setEditingMapping(null);
    setFormData({ ...emptyForm, sort_order: mappings.length + 1 });
    setOpenDialog(true);
  };

  const handleEdit = (mapping: FieldMapping) => {
    setEditingMapping(mapping);
    setFormData({
      form_field:        mapping.form_field,
      label:             mapping.label || '',
      template_variable: mapping.template_variable,
      field_type:        mapping.field_type,
      section:           mapping.section || '',
      is_required:       mapping.is_required,
      is_hidden:         mapping.is_hidden,
      description:       mapping.description || '',
      sort_order:        mapping.sort_order,
    });
    setOpenDialog(true);
  };

  const handleDelete = async (mappingId: number) => {
    if (!window.confirm('Delete this field mapping?')) return;
    try {
      await fieldMappingService.deleteMapping(intakeFormTypeId, mappingId);
      setMappings(prev => prev.filter(m => m.id !== mappingId));
      toast.success('Field mapping deleted');
    } catch {
      toast.error('Failed to delete field mapping');
    }
  };

  const handleSubmit = async () => {
    setLoading(true);
    try {
      if (editingMapping) {
        await fieldMappingService.update(intakeFormTypeId, editingMapping.id, {
          ...formData, id: editingMapping.id,
        });
        toast.success('Field mapping updated');
      } else {
        await fieldMappingService.create(intakeFormTypeId, formData);
        toast.success('Field mapping created');
      }
      setOpenDialog(false);
      loadMappings();
    } catch {
      toast.error('Failed to save field mapping');
    } finally {
      setLoading(false);
    }
  };

  // ── Section visibility ────────────────────────────────────────────────────
  const handleSaveSectionSettings = async () => {
    setSavingSections(true);
    try {
      const updateData: UpdateSectionSettingsData = {
        sections: sectionSettings.map(s => ({
          section: s.section, is_hidden: s.is_hidden, sort_order: s.sort_order,
        })),
      };
      await fieldMappingService.updateSectionSettings(intakeFormTypeId, updateData);
      toast.success('Section visibility saved');
      await loadSectionSettings();
    } catch {
      toast.error('Failed to save section settings');
    } finally {
      setSavingSections(false);
    }
  };

  // ── Helpers ───────────────────────────────────────────────────────────────
  const getSectionLabel = (key: string) =>
    key === '__none__' ? 'No Section' : (sections[key] || key.replace(/_/g, ' '));

  const getFieldTypeLabel = (key: string) => fieldTypes[key] || key;

  const FIELD_TYPE_COLORS: Record<string, any> = {
    text: 'default', number: 'secondary', date: 'primary', boolean: 'warning',
    email: 'info', phone: 'info', address: 'default', signature: 'error', array: 'success',
  };

  // ── Render ────────────────────────────────────────────────────────────────
  return (
    <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
      {/* ── Header ── */}
      <Box
        sx={{
          px: 3, py: 2,
          borderBottom: '1px solid', borderColor: 'divider',
          display: 'flex', alignItems: 'center', justifyContent: 'space-between',
          bgcolor: 'background.paper',
          flexShrink: 0,
        }}
      >
        <Box>
          <Typography variant='h6' fontWeight={700}>{intakeFormTypeName}</Typography>
          <Typography variant='caption' color='text.secondary'>
            {mappings.length} field mapping{mappings.length !== 1 ? 's' : ''}
          </Typography>
        </Box>
        <Button variant='contained' startIcon={<AddIcon />} onClick={handleCreate}>
          Add Field Mapping
        </Button>
      </Box>

      {/* ── Tabs ── */}
      <Box sx={{ borderBottom: 1, borderColor: 'divider', bgcolor: 'background.paper', flexShrink: 0 }}>
        <Tabs value={activeTab} onChange={(_, v) => setActiveTab(v)} sx={{ px: 2 }}>
          <Tab label={`Field Mappings (${mappings.length})`} />
          <Tab label={`Section Visibility (${sectionSettings.filter(s => !s.is_hidden).length} visible)`} />
        </Tabs>
      </Box>

      {/* ── Tab 0: Field Mappings ── */}
      {activeTab === 0 && (
        <Box sx={{ flex: 1, overflowY: 'auto', p: 2 }}>
          {/* Search bar */}
          <TextField
            size='small'
            placeholder='Search by field name, label or variable…'
            value={search}
            onChange={e => setSearch(e.target.value)}
            sx={{ mb: 2, width: { xs: '100%', sm: 360 } }}
            InputProps={{
              startAdornment: (
                <InputAdornment position='start'>
                  <SearchIcon fontSize='small' />
                </InputAdornment>
              ),
            }}
          />

          {loading ? (
            <Box display='flex' justifyContent='center' py={6}><CircularProgress /></Box>
          ) : sectionKeys.length === 0 ? (
            <Paper sx={{ p: 4, textAlign: 'center' }}>
              <Typography color='text.secondary'>
                {search ? 'No fields match your search.' : 'No field mappings yet. Click "Add Field Mapping" to start.'}
              </Typography>
            </Paper>
          ) : (
            sectionKeys.map(sectionKey => {
              const sectionMappings = groupedMappings[sectionKey];
              const icon = SECTION_ICONS[sectionKey] || '📋';
              const label = getSectionLabel(sectionKey);
              const isExpanded = expandedSections.has(sectionKey);
              const requiredCount = sectionMappings.filter(m => m.is_required).length;
              const hiddenCount   = sectionMappings.filter(m => m.is_hidden).length;

              return (
                <Accordion
                  key={sectionKey}
                  expanded={isExpanded}
                  onChange={() => {
                    const next = new Set(expandedSections);
                    isExpanded ? next.delete(sectionKey) : next.add(sectionKey);
                    setExpandedSections(next);
                  }}
                  sx={{ mb: 1, '&:before': { display: 'none' }, borderRadius: '8px !important', border: '1px solid', borderColor: 'divider' }}
                  elevation={0}
                >
                  <AccordionSummary expandIcon={<ExpandMoreIcon />} sx={{ borderRadius: 2 }}>
                    <Box display='flex' alignItems='center' gap={1.5} flex={1}>
                      <Typography sx={{ fontSize: '1.1rem' }}>{icon}</Typography>
                      <Typography fontWeight={600}>{label}</Typography>
                      <Chip label={`${sectionMappings.length} fields`} size='small' sx={{ ml: 1 }} />
                      {requiredCount > 0 && (
                        <Chip
                          label={`${requiredCount} required`}
                          size='small'
                          color='error'
                          variant='outlined'
                          icon={<CheckCircleIcon sx={{ fontSize: '0.9rem !important' }} />}
                        />
                      )}
                      {hiddenCount > 0 && (
                        <Chip
                          label={`${hiddenCount} hidden`}
                          size='small'
                          color='warning'
                          variant='outlined'
                          icon={<HiddenIcon sx={{ fontSize: '0.9rem !important' }} />}
                        />
                      )}
                    </Box>
                  </AccordionSummary>

                  <AccordionDetails sx={{ p: 0 }}>
                    <TableContainer>
                      <Table size='small'>
                        <TableHead>
                          <TableRow sx={{ '& th': { fontWeight: 600, bgcolor: 'grey.50', fontSize: '0.75rem' } }}>
                            <TableCell>Form Field</TableCell>
                            <TableCell>Label</TableCell>
                            <TableCell>Type</TableCell>
                            <TableCell align='center'>Required</TableCell>
                            <TableCell align='center'>Visible</TableCell>
                            <TableCell align='right' sx={{ width: 90 }}>Actions</TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {sectionMappings
                            .sort((a, b) => a.sort_order - b.sort_order)
                            .map(mapping => (
                            <TableRow
                              key={mapping.id}
                              hover
                              sx={{ opacity: mapping.is_hidden ? 0.55 : 1 }}
                            >
                              <TableCell>
                                <Typography
                                  variant='body2'
                                  sx={{ fontFamily: 'monospace', fontSize: '0.78rem', color: 'primary.main' }}
                                >
                                  {mapping.form_field.includes('.')
                                    ? mapping.form_field.split('.').slice(1).join('.')
                                    : mapping.form_field}
                                </Typography>
                              </TableCell>
                              <TableCell>
                                <Typography variant='body2' fontWeight={500}>
                                  {mapping.label || <span style={{ color: '#aaa' }}>—</span>}
                                </Typography>
                              </TableCell>
                              <TableCell>
                                <Chip
                                  label={getFieldTypeLabel(mapping.field_type)}
                                  size='small'
                                  color={FIELD_TYPE_COLORS[mapping.field_type] || 'default'}
                                  variant='outlined'
                                  sx={{ fontSize: '0.7rem', height: 20 }}
                                />
                              </TableCell>
                              <TableCell align='center'>
                                <Tooltip title={mapping.is_required ? 'Required — click to make optional' : 'Optional — click to make required'}>
                                  <Switch
                                    size='small'
                                    checked={mapping.is_required}
                                    onChange={() => handleToggle(mapping, 'is_required')}
                                    disabled={togglingId === mapping.id}
                                    color='error'
                                  />
                                </Tooltip>
                              </TableCell>
                              <TableCell align='center'>
                                <Tooltip title={mapping.is_hidden ? 'Hidden — click to show' : 'Visible — click to hide'}>
                                  <Switch
                                    size='small'
                                    checked={!mapping.is_hidden}
                                    onChange={() => handleToggle(mapping, 'is_hidden')}
                                    disabled={togglingId === mapping.id}
                                    color='success'
                                  />
                                </Tooltip>
                              </TableCell>
                              <TableCell align='right'>
                                <Tooltip title='Edit'>
                                  <IconButton size='small' onClick={() => handleEdit(mapping)}>
                                    <EditIcon fontSize='small' />
                                  </IconButton>
                                </Tooltip>
                                <Tooltip title='Delete'>
                                  <IconButton size='small' color='error' onClick={() => handleDelete(mapping.id)}>
                                    <DeleteIcon fontSize='small' />
                                  </IconButton>
                                </Tooltip>
                              </TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </TableContainer>
                  </AccordionDetails>
                </Accordion>
              );
            })
          )}
        </Box>
      )}

      {/* ── Tab 1: Section Visibility ── */}
      {activeTab === 1 && (
        <Box sx={{ flex: 1, overflowY: 'auto', p: 3 }}>
          <Box display='flex' justifyContent='space-between' alignItems='center' mb={3}>
            <Box>
              <Typography variant='subtitle1' fontWeight={600}>Section Visibility</Typography>
              <Typography variant='caption' color='text.secondary'>
                Toggle which sections appear in the intake form for this form type.
              </Typography>
            </Box>
            <Button
              variant='contained'
              onClick={handleSaveSectionSettings}
              disabled={savingSections || loadingSections}
            >
              {savingSections ? 'Saving…' : 'Save Changes'}
            </Button>
          </Box>

          {loadingSections ? (
            <Box display='flex' justifyContent='center' py={4}><CircularProgress /></Box>
          ) : (
            <Box
              sx={{
                display: 'grid',
                gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr', md: '1fr 1fr 1fr' },
                gap: 2,
              }}
            >
              {sectionSettings.map(setting => {
                const icon = SECTION_ICONS[setting.section] || '📋';
                return (
                  <Paper
                    key={setting.section}
                    variant='outlined'
                    sx={{
                      p: 2,
                      display: 'flex',
                      alignItems: 'center',
                      gap: 2,
                      borderRadius: 2,
                      borderColor: setting.is_hidden ? 'warning.light' : 'success.light',
                      bgcolor: setting.is_hidden ? 'warning.50' : 'success.50',
                      opacity: setting.is_hidden ? 0.7 : 1,
                      transition: 'all 0.2s',
                    }}
                  >
                    <Typography sx={{ fontSize: '1.4rem', lineHeight: 1 }}>{icon}</Typography>
                    <Box flex={1}>
                      <Typography variant='body2' fontWeight={600}>{setting.section_name}</Typography>
                      <Chip
                        size='small'
                        label={setting.is_hidden ? 'Hidden' : 'Visible'}
                        color={setting.is_hidden ? 'warning' : 'success'}
                        sx={{ mt: 0.5, height: 18, fontSize: '0.68rem' }}
                        icon={setting.is_hidden
                          ? <HiddenIcon sx={{ fontSize: '0.8rem !important' }} />
                          : <VisibleIcon sx={{ fontSize: '0.8rem !important' }} />}
                      />
                    </Box>
                    <Switch
                      checked={!setting.is_hidden}
                      onChange={e =>
                        setSectionSettings(prev =>
                          prev.map(s =>
                            s.section === setting.section
                              ? { ...s, is_hidden: !e.target.checked }
                              : s
                          )
                        )
                      }
                      color='success'
                    />
                  </Paper>
                );
              })}
            </Box>
          )}
        </Box>
      )}

      {/* ── Add / Edit dialog ── */}
      <Dialog open={openDialog} onClose={() => setOpenDialog(false)} maxWidth='sm' fullWidth>
        <DialogTitle sx={{ pb: 1 }}>
          {editingMapping ? '✏️ Edit Field Mapping' : '➕ Add Field Mapping'}
        </DialogTitle>
        <DialogContent>
          <Stack spacing={2.5} sx={{ mt: 1 }}>
            <Box display='flex' gap={2}>
              <TextField
                fullWidth
                label='Form Field'
                value={formData.form_field}
                onChange={e => setFormData(p => ({ ...p, form_field: e.target.value }))}
                helperText="e.g. personal_info.first_name"
                required
                size='small'
              />
              <TextField
                fullWidth
                label='Label'
                value={formData.label}
                onChange={e => setFormData(p => ({ ...p, label: e.target.value }))}
                helperText="Display name, e.g. First Name"
                size='small'
              />
            </Box>

            <TextField
              fullWidth
              label='Template Variable'
              value={formData.template_variable}
              onChange={e => setFormData(p => ({ ...p, template_variable: e.target.value.toUpperCase() }))}
              helperText="e.g. FIRST_NAME"
              required
              size='small'
            />

            <Box display='flex' gap={2}>
              <FormControl fullWidth size='small'>
                <InputLabel>Field Type</InputLabel>
                <Select
                  value={formData.field_type}
                  onChange={e => setFormData(p => ({ ...p, field_type: e.target.value }))}
                  label='Field Type'
                >
                  {Object.entries(fieldTypes).map(([key, val]) => (
                    <MenuItem key={key} value={key}>{val}</MenuItem>
                  ))}
                </Select>
              </FormControl>

              <FormControl fullWidth size='small'>
                <InputLabel>Section</InputLabel>
                <Select
                  value={formData.section}
                  onChange={e => setFormData(p => ({ ...p, section: e.target.value }))}
                  label='Section'
                >
                  <MenuItem value=''>— None —</MenuItem>
                  {Object.entries(sections).map(([key, val]) => (
                    <MenuItem key={key} value={key}>{SECTION_ICONS[key] || '📋'} {val}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Box>

            <Box display='flex' gap={2} alignItems='center'>
              <TextField
                label='Sort Order'
                type='number'
                value={formData.sort_order}
                onChange={e => setFormData(p => ({ ...p, sort_order: parseInt(e.target.value) || 0 }))}
                size='small'
                sx={{ width: 130 }}
              />
              <FormControlLabel
                control={
                  <Switch
                    checked={!!formData.is_required}
                    onChange={e => setFormData(p => ({ ...p, is_required: e.target.checked }))}
                    color='error'
                  />
                }
                label='Required'
              />
              <FormControlLabel
                control={
                  <Switch
                    checked={!!formData.is_hidden}
                    onChange={e => setFormData(p => ({ ...p, is_hidden: e.target.checked }))}
                    color='warning'
                  />
                }
                label='Hidden'
              />
            </Box>

            <TextField
              fullWidth
              label='Description'
              value={formData.description}
              onChange={e => setFormData(p => ({ ...p, description: e.target.value }))}
              multiline
              rows={2}
              size='small'
              helperText='Optional notes about this field'
            />
          </Stack>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setOpenDialog(false)}>Cancel</Button>
          <Button
            variant='contained'
            onClick={handleSubmit}
            disabled={loading || !formData.form_field || !formData.template_variable}
          >
            {loading ? <CircularProgress size={18} /> : (editingMapping ? 'Update' : 'Create')}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default FieldMappingManager;
